import { useCallback, useContext, useEffect, useState } from 'react';
import { GlobalContext } from '../context/GlobalContext';
import { UserData } from './auth';
import auth from '../auth';
import { getActiveImpersonationSession, getImpersonationHeaders } from '../utils/impersonation';
import {
	SellerCommissionBankAccount,
	SellerCommissionCampaign,
	SellerCommissionCampaignInput,
	SellerCommissionCampaignsResponse,
	SellerCommissionCampaignResults,
	SellerCommissionDashboard,
	SellerCommissionPayoutRequest,
	SellerCommissionPayoutRequestsResponse,
	SellerCommissionPayoutTransitionStatus,
	SellerCommissionPayoutUpdateResponse,
	SellerCommissionTargetsResponse
} from '../types/seller-commissions';
import { toApiUrl } from '../utils/apiBase';
import { get_pos_headers } from '../utils/embeddedSale';

export default function useFetch<T extends keyof API, V extends EndpointVerbs<T>>(
	endpoint: T,
	verb: V,
	condition: boolean,
	initPayload?: APIRequestBody<T, V>,
	setCustomLoading?: (value: boolean) => void,
	token_necessary = true
) {
	const { userData, accessToOtherStores, internalCountry } = useContext(GlobalContext);

	const [refetch, setRefetch] = useState(0);

	const [payload, setPayload] = useState(initPayload ? initPayload : null);

	const [fetched, setFetched] = useState(false);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<boolean | string>(false);
	const [data, setData] = useState<null | APIResponse<T, V>>(null);

	const useTestAuth =
		accessToOtherStores &&
		((endpoint === 'https://api.krece.app/storeprofile/masteruser/' && verb === 'GET') ||
			(endpoint === 'https://api.krece.app/storeprofile/get_custom_token/' && verb === 'POST'));

	useEffect(() => {
		let active = true;
		const controller = verb === 'GET' ? new AbortController() : null;

		(async () => {
			const pos_headers = get_pos_headers(toApiUrl(endpoint));
			const token = (await auth?.currentUser?.getIdToken(true)) || userData?.firebase_token;
			if ((controller && !active) || (!token && token_necessary) || !condition) return;
			const impersonationHeaders = getImpersonationHeaders(getActiveImpersonationSession(userData?.email));

			setCustomLoading?.(true);
			setLoading(true);
			setError(false);
			setFetched(false);

			const url = new URL(toApiUrl(endpoint));
			if (payload && verb === 'GET') {
				Object.keys(payload).forEach((key) => {
					const value = (payload as any)[key];
					if (value !== undefined && value !== null) {
						url.searchParams.append(key, value);
					}
				});
			}
			fetch(url, {
				method: verb,
				...(controller && { signal: controller.signal }),
				...(payload && verb !== 'GET' && { body: JSON.stringify(payload) }),
				headers: {
					...(token && { Authorization: `Bearer ${useTestAuth ? accessToOtherStores?.token || token : token}` }),
					...(verb !== 'GET' && { 'Content-Type': 'application/json' }),
					...(userData?.master_store?.id && {
						'X-Master-Store-Email':
							!useTestAuth || !accessToOtherStores || !accessToOtherStores?.email
								? userData.master_store.id
								: accessToOtherStores?.email
					}),
					...(userData?.email && {
						'X-User-Email':
							!useTestAuth || !accessToOtherStores || !accessToOtherStores?.email ? userData.email : accessToOtherStores?.email
					}),
					...(userData?.country && { 'X-Country': userData.country }),
					...(internalCountry.get() && { 'X-Country': internalCountry.get() }),
					...impersonationHeaders,
					...pos_headers
				}
			})
				.then(async (response) => {
					if (!response.ok) {
						const error = await response.json();
						if (typeof error === 'object' && 'status' in error && typeof error.status === 'string') throw error.status;
						else throw new Error();
					} else {
						return response.json();
					}
				})
				.then((data: APIResponse<T, V> | { success: boolean; status: string }) => {
					if ((controller && !active) || !data) return;
					if (typeof data === 'object' && 'success' in data && !data.success) {
						if (data?.status && typeof data.status === 'string') setError(data.status);
						else setError('Ha ocurrido un error. Intente más tarde.');
					} else {
						const response = typeof data === 'object' && (('data' in data) as any) ? (data as any).data : data;
						setData(response);
					}
				})
				.catch((error) => {
					if (controller && (!active || (error instanceof DOMException && error.name === 'AbortError'))) return;
					if (typeof error === 'string') setError(error);
					else setError('Ha ocurrido un error. Intente más tarde.');
				})
				.finally(() => {
					if (controller && !active) return;
					setCustomLoading?.(false);
					setLoading(false);
					setFetched(true);
				});
		})();

		return () => {
			active = false;
			if (controller) {
				controller.abort();
				setCustomLoading?.(false);
				setLoading(false);
			}
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [payload, condition, refetch]);

	const refetchData = useCallback(() => setRefetch((value) => value + 1), []);

	return {
		loading,
		data,
		error,
		setPayload,
		payload,
		fetched,
		refetch: refetchData,
		reset: () => {
			setData(null);
			setError(false);
			setFetched(false);
			setLoading(false);
			setCustomLoading?.(false);
		}
	};
}

export async function unsafeAPI(
	verb: HTTPVerbs,
	endpoint: string,
	payload: any,
	userData: UserData | null,
	internalCountry: string | null,
	token_necessary = true,
	downloadFile = false,
	signal?: AbortSignal
) {
	if (!userData && token_necessary) return null;

	try {
		const impersonationHeaders = getImpersonationHeaders(getActiveImpersonationSession(userData?.email));
		const pos_headers = get_pos_headers(toApiUrl(endpoint));
		const response = await fetch(toApiUrl(endpoint), {
			method: verb,
			signal,
			...(payload && typeof payload === 'object' && { body: JSON.stringify(payload) }),
			headers: {
				...(payload && typeof payload === 'object' && { 'Content-Type': 'application/json' }),
				...(userData?.firebase_token && {
					Authorization: `Bearer ${(await auth?.currentUser?.getIdToken(true)) || userData.firebase_token}`
				}),
				...(userData?.master_store?.id && { 'X-Master-Store-Email': userData.master_store.id }),
				...(userData?.email && { 'X-User-Email': userData.email }),
				...(userData?.country && { 'X-Country': userData.country }),
				...(internalCountry && { 'X-Country': internalCountry }),
				...impersonationHeaders,
				...pos_headers
			}
		});

		if (downloadFile) {
			// Handle file download
			const blob = await response.blob();
			const contentDisposition = response.headers.get('Content-Disposition');
			let filename = 'download';

			if (contentDisposition) {
				const filenameMatch = contentDisposition.match(/filename="(.+)"/);
				if (filenameMatch) {
					filename = filenameMatch[1];
				}
			}

			const url = window.URL.createObjectURL(blob);
			const a = document.createElement('a');
			a.href = url;
			a.download = filename;
			document.body.appendChild(a);
			a.click();
			window.URL.revokeObjectURL(url);
			document.body.removeChild(a);

			return { success: true };
		}

		return await response.json();
	} catch (error) {
		console.error(error);
		return null;
	}
}

export interface APIEnvelope<T> {
	// Supplied by the transport, never trusted from the response body.
	http_status: number;
	success: boolean;
	status: string;
	data?: T;
	code?: string;
	processing?: boolean;
	request_id?: string;
}

export function callAPI<T extends keyof API, V extends EndpointVerbs<T>>(
	endpoint: T,
	verb: V,
	payload: APIRequestBody<T, V>,
	userData: UserData | null,
	country?: string | null,
	token_necessary?: boolean,
	force_token_refresh?: boolean,
	signal?: AbortSignal,
	response_mode?: 'flatten'
): Promise<[APIResponse<T, V>, null] | [null, string]>;
export function callAPI<T extends keyof API, V extends EndpointVerbs<T>>(
	endpoint: T,
	verb: V,
	payload: APIRequestBody<T, V>,
	userData: UserData | null,
	country: string | null | undefined,
	token_necessary: boolean | undefined,
	force_token_refresh: boolean | undefined,
	signal: AbortSignal | undefined,
	response_mode: 'envelope'
): Promise<[APIEnvelope<APIResponse<T, V>>, null] | [null, string]>;
export async function callAPI<T extends keyof API, V extends EndpointVerbs<T>>(
	endpoint: T,
	verb: V,
	payload: APIRequestBody<T, V>,
	userData: UserData | null,
	country: string | null = null,
	token_necessary = true,
	force_token_refresh = true,
	signal?: AbortSignal,
	response_mode: 'flatten' | 'envelope' = 'flatten'
): Promise<[APIResponse<T, V> | APIEnvelope<APIResponse<T, V>>, null] | [null, string]> {
	try {
		const pos_headers = get_pos_headers(toApiUrl(endpoint));
		const token = (await auth?.currentUser?.getIdToken(force_token_refresh)) || userData?.firebase_token;
		if (!token && token_necessary) {
			return [null, 'Token de autenticación requerido'];
		}
		const impersonationHeaders = getImpersonationHeaders(getActiveImpersonationSession(userData?.email));
		const isFormData =
			typeof FormData !== 'undefined' &&
			typeof payload === 'object' &&
			payload !== null &&
			FormData.prototype.isPrototypeOf(payload);

		const url = new URL(toApiUrl(endpoint));
		if (payload && verb === 'GET') {
			Object.keys(payload).forEach((key) => {
				const value = (payload as any)[key];
				if (value !== undefined && value !== null) {
					url.searchParams.append(key, value);
				}
			});
		}

		const response = await fetch(url, {
			method: verb,
			signal,
			...(payload && verb !== 'GET' && { body: isFormData ? payload : JSON.stringify(payload) }),
			headers: {
				...(token && { Authorization: `Bearer ${token}` }),
				...(verb !== 'GET' && !isFormData && { 'Content-Type': 'application/json' }),
				...(userData?.master_store?.id && { 'X-Master-Store-Email': userData.master_store.id }),
				...(userData?.email && { 'X-User-Email': userData.email }),
				...(userData?.country && { 'X-Country': userData.country }),
				...(country && { 'X-Country': country }),
				...impersonationHeaders,
				...pos_headers
			}
		});

		// Opt in when a workflow needs processing flags and structured rejection codes,
		// including 202 responses whose success flag is false until finalization.
		if (response_mode === 'envelope') {
			const envelope: unknown = await response.json();
			if (!envelope || typeof envelope !== 'object' || Array.isArray(envelope)) {
				return [null, 'Ha ocurrido un error. Intente más tarde.'];
			}
			return [{ ...envelope, http_status: response.status } as APIEnvelope<APIResponse<T, V>>, null];
		}

		if (!response.ok) {
			const error = await response.json();
			if (typeof error === 'object' && 'status' in error && typeof error.status === 'string') {
				return [null, error.status];
			} else {
				return [null, 'Ha ocurrido un error. Intente más tarde.'];
			}
		}

		const data: APIResponse<T, V> | { success: boolean; status: string } = await response.json();

		if (!data) {
			return [null, 'Ha ocurrido un error. Intente más tarde.'];
		}

		if (typeof data === 'object' && 'success' in data && !data.success) {
			if (data?.status && typeof data.status === 'string') {
				return [null, data.status];
			} else {
				return [null, 'Ha ocurrido un error. Intente más tarde.'];
			}
		}

		const processedResponse = typeof data === 'object' && (('data' in data) as any) ? (data as any).data : data;
		return [processedResponse as APIResponse<T, V>, null];
	} catch (error) {
		return [null, 'Ha ocurrido un error. Intente más tarde.'];
	}
}

type HTTPVerbs = 'POST' | 'GET' | 'PUT' | 'DELETE' | 'PATCH';
type EndpointVerbs<T extends keyof API> = keyof API[T] & HTTPVerbs;
type APIRequestBody<T extends keyof API, V extends EndpointVerbs<T>> = API[T][V] extends { request: any }
	? API[T][V]['request']
	: never;
type APIResponse<T extends keyof API, V extends EndpointVerbs<T>> = API[T][V] extends { response: any }
	? API[T][V]['response']
	: API[T][V];

// A customer's own financing conditions (Users.financing_overrides), as the backend normalizes them.
// A set key replaces the customer's level value for that product; absent keys keep it.
export type FinancingOverrideProduct = {
	payment_options?: { installments: number; factor: number }[];
	/** Integer percentages (0-100) */
	down_payment?: { min: number; max: number; granularity: number };
	price_range?: { min: number; max: number };
	min_amount?: number;
	fee?: number;
};
export type FinancingOverrideProductType =
	| 'phone'
	| 'iphone'
	| 'tablet'
	| 'laptop'
	| 'other'
	| 'services'
	| 'insurance'
	| 'loan'
	| 'recarga';
export type ActiveFinancingOverrides = Partial<Record<FinancingOverrideProductType, FinancingOverrideProduct>>;

export type StoreHealthStatus = 'GOOD' | 'NEUTRAL' | 'BAD' | 'CRITICAL' | 'NO_DATA';
export type EliteStoreStatus = 'ELITE_STABLE' | 'ELITE_AT_RISK' | 'NEAR_ELITE' | 'STANDARD' | 'NOT_ELIGIBLE';
export type EliteQualificationType = 'PORTFOLIO' | 'TOP_SALES';

export type ElitePaymentOption = {
	installments: number;
	factor: number;
	interest_percent: number;
	elite: true;
};

export type EliteLevelBenefit = {
	down_payment_percent: number | null;
	additional_payment_options: ElitePaymentOption[];
	standard_down_payment_percent?: number | null;
	standard_max_installments?: number | null;
};

export type EliteBenefitsByLevel = Record<string, Record<string, EliteLevelBenefit>>;

export type EliteStoreMembership = {
	status: EliteStoreStatus;
	benefits_active?: boolean;
	qualification_type?: EliteQualificationType | null;
	qualification_paths?: EliteQualificationType[];
	country_rank?: number | null;
	eligible_store_count?: number;
	cutoff_rank?: number | null;
	cutoff_delinquency_15d?: number | null;
	sales_count_180d?: number;
	sales_country_rank?: number | null;
	sales_cutoff_rank?: number | null;
	sales_delinquency_rank?: number | null;
	sales_delinquency_cutoff_rank?: number | null;
	sales_delinquency_cutoff_15d?: number | null;
	sales_top_percentage?: number | null;
	sales_delinquency_top_percentage?: number | null;
	sales_lookback_days?: number;
	minimum_evaluable_installments?: number;
	evaluable_installments?: number;
	restricted_branches?: Array<{ branch: string }>;
	config_version?: number | string;
	config_signature?: string;
	run_id?: string;
};

export type StoreHealthSummary = {
	can_view_details: boolean;
	health_status: StoreHealthStatus;
	elite_status: EliteStoreStatus;
	benefits_active: boolean;
	branch_restricted: boolean;
	elite_benefits: EliteBenefitsByLevel;
	elite_qualification_type?: EliteQualificationType | null;
	elite_top_percentage?: number | null;
	elite_sales_top_percentage?: number | null;
	elite_sales_delinquency_top_percentage?: number | null;
	elite_sales_lookback_days?: number;
	elite_position_percent?: number | null;
	as_of_date?: string;
	metrics?: StoreHealthMetrics;
	health?: StoreHealthMetadata;
	elite_program?: EliteStoreMembership;
	history?: StoreHealthHistoryPoint[];
};

export type EliteFinancingOptions = {
	elite_status: EliteStoreStatus;
	benefits_active: boolean;
	branch_restricted: boolean;
	benefits_by_level: EliteBenefitsByLevel;
	config_version?: number | string;
};

export type StoreHealthMetrics = {
	sales_count_180d?: number | null;
	payments_due_count: number | null;
	unpaid_payments_due_count: number | null;
	amount_overdue: number | null;
	evaluable_15d: number | null;
	delinquent_15d: number | null;
	delinquency_15d: number | null;
	evaluable_20d: number | null;
	delinquent_20d: number | null;
	delinquency_20d: number | null;
	evaluable_45d: number | null;
	delinquent_45d: number | null;
	delinquency_45d: number | null;
};

export type StoreHealthBreakdown = {
	label: string;
	payments_due_count: number;
	unpaid_payments_due_count: number;
	amount_overdue: number;
	evaluable_15d: number;
	delinquent_15d: number;
	delinquency_15d: number | null;
	evaluable_45d: number;
	delinquent_45d: number;
	delinquency_45d: number | null;
};

export type StoreHealthMetadata = {
	status?: StoreHealthStatus;
	score?: number | null;
	low_sample?: boolean;
	country_percentile?: number | null;
	country_thresholds?: {
		low?: number | null;
		high?: number | null;
		window_days?: number;
	};
	country_benchmarks?: {
		avg_15d?: number | null;
		median_15d?: number | null;
		p75_15d?: number | null;
		threshold_low_15d?: number | null;
		threshold_high_15d?: number | null;
		avg_20d?: number | null;
		median_20d?: number | null;
		avg_45d?: number | null;
		median_45d?: number | null;
		stores_evaluated?: number;
	};
};

export type StoreHealthSnapshot = {
	store_email: string;
	store_name?: string;
	country?: string;
	as_of_date?: string;
	lookback_days?: number;
	is_legacy_fallback?: boolean;
	metrics: StoreHealthMetrics;
	health: StoreHealthMetadata;
	elite_program?: EliteStoreMembership;
	elite_benefits?: EliteBenefitsByLevel;
	by_model?: StoreHealthBreakdown[];
	by_seller?: StoreHealthBreakdown[];
	by_branch?: StoreHealthBreakdown[];
};

export type StoreHealthHistoryPoint = {
	as_of_date: string;
	metrics: StoreHealthMetrics;
	health: StoreHealthMetadata;
};

export type WarrantyDevice = {
	device_document_id: string;
	device_type: 'iphone' | 'phone' | 'tablet';
	device_imei_no?: string;
	device_model?: string;
	device_manufacturer?: string;
	device_commercial_name?: string;
	device_management_provider?: string;
	device_confirmation_id?: number | string | null;
	nuovopay_device_id?: number | string | null;
	device_price: number;
	amount_financed: number;
	payments_amount: number;
	device_payments_left: number;
	loan_duration: number;
	loan_payment_cycle: number;
	self_financed: boolean;
	seller_store: string;
	sub_store: string;
	store_user?: string;
	country?: string;
	date_bought?: string | null;
	is_active?: boolean;
	is_released?: boolean;
	warranty_mode?: 'active' | 'fully_paid' | null;
	release_reason?: string | null;
	release_time?: string | null;
	warranty_old_imei_release_pending?: boolean;
	warranty_last_target_imei?: string | null;
};

export type StorePopupRecord = {
	id: string;
	country: string;
	title: string;
	subtitle?: string | null;
	image_url?: string | null;
	redirect_url?: string | null;
	button_text?: string | null;
	is_active: boolean;
	master_stores_only: boolean;
	append_store_email_to_url: boolean;
	dismiss_interval_days: number;
	display_version: number;
	created_at?: string;
	created_by?: string;
	updated_at?: string;
	updated_by?: string;
	activated_at?: string;
	activated_by?: string;
	deactivated_at?: string;
	deactivated_by?: string;
};

export type StorePopupPayload = {
	country?: string;
	title?: string;
	subtitle?: string | null;
	image_url?: string | null;
	redirect_url?: string | null;
	button_text?: string | null;
	is_active?: boolean;
	master_stores_only?: boolean;
	append_store_email_to_url?: boolean;
	dismiss_interval_days?: number;
	reset_frequency?: boolean;
};

export type StoreWebhookEventType = 'sale.completed' | 'sale.cancelled';

export type StoreWebhookConfig = {
	url: string;
	is_active: boolean;
	event_types: StoreWebhookEventType[];
	has_signing_secret: boolean;
	signing_secret_version: number | null;
	events_enabled_at: string | null;
	last_tested_at: string | null;
	created_at: string | null;
	updated_at: string | null;
	updated_by: string | null;
};

export type StoreWebhookDelivery = {
	id: string;
	event_id: string | null;
	event_type: StoreWebhookEventType | null;
	sale_id: string | null;
	status: 'pending' | 'delivering' | 'delivered' | 'failed';
	attempt_count: number;
	last_response_status: number | null;
	last_error_code: string | null;
	last_response_excerpt: string | null;
	created_at: string | null;
	last_attempt_at: string | null;
	delivered_at: string | null;
};

export interface IdentityRecoveryRetry {
	request_id: string;
	email: string;
	expected_identification_number: string;
}

export interface IdentityRecoveryResult {
	country: string;
	identification_number: string;
	email: string;
	amount_delinquent: number;
	amount_delinquent_financed: number;
	currency: string;
}

export interface API {

	'https://api.krece.app/registration/eligiblepromotions/': {
		POST: {
			request: { user_document_id?: string };
			response: {
				restricted_promotion_codes: string[];
				eligible_promotion_codes: string[];
			};
		};
	};
	'https://api.krece.app/administration/identity-recovery/': {
		POST: {
			request: FormData | IdentityRecoveryRetry;
			response: IdentityRecoveryResult;
		};
	};
	'https://api.krece.app/storeprofile/seller_commissions/campaigns/': {
		GET: {
			request: { country: string; include_targets?: boolean };
			response: SellerCommissionCampaignsResponse;
		};
		POST: {
			request: SellerCommissionCampaignInput;
			response: SellerCommissionCampaign;
		};
		PUT: {
			request: SellerCommissionCampaignInput & { id: string };
			response: SellerCommissionCampaign;
		};
		PATCH: {
			request: { id: string };
			response: { id: string; status: 'stopped'; stopped_at: string };
		};
	};
	'https://api.krece.app/storeprofile/seller_commissions/targets/': {
		GET: {
			request: { country: string; search?: string; batch_size?: number; cursor?: string; master_store_email?: string };
			response: SellerCommissionTargetsResponse;
		};
	};
	'https://api.krece.app/storeprofile/seller_commissions/dashboard/': {
		GET: {
			request: { campaign_id?: string; seller_email?: string; batch_size?: number; cursor?: string; page_index?: number } | undefined;
			response: SellerCommissionDashboard;
		};
	};
	'https://api.krece.app/storeprofile/seller_commissions/campaign_results/': {
		GET: {
			request: {
				country: string;
				campaign_id: string;
				master_store_email?: string;
				seller_email?: string;
				batch_size?: number;
				cursor?: string;
				page_index?: number;
			};
			response: SellerCommissionCampaignResults;
		};
	};
	'https://api.krece.app/storeprofile/seller_commissions/bank_account/': {
		GET: {
			request: undefined;
			response: SellerCommissionBankAccount;
		};
		PUT: {
			request: {
				bank: string;
				type: string;
				holder: string;
				account: string;
				identity_number: string;
				identity_type: string;
				interbank_code?: string;
			};
			response: SellerCommissionBankAccount;
		};
	};
	'https://api.krece.app/storeprofile/seller_commissions/payout_requests/': {
		GET: {
			request: { country: string; master_store_email?: string; status?: string; batch_size?: number; offset?: number; cursor?: string };
			response: SellerCommissionPayoutRequestsResponse;
		};
		POST: {
			request: { campaign_id: string };
			response: SellerCommissionPayoutRequest;
		};
		PATCH: {
			request: { id: string; status: SellerCommissionPayoutTransitionStatus; notes?: string } | FormData;
			response: SellerCommissionPayoutUpdateResponse;
		};
	};
	'https://api.krece.app/storeprofile/seller_commissions/payout_receipt/': {
		GET: {
			request: { id: string };
			response: { download_url: string; filename: string; expires_at: string };
		};
	};
	'https://api.krece.app/report_device_stolen/': {
		POST: {
			request: { device_id: string; is_stolen: boolean };
			response: { success: boolean; status: string };
		};
	};
	'https://api.krece.app/users/isnewuser/': {
		POST: {
			request: { user_document_id: string };
			response: { is_new_user: boolean };
		};
	};
	'https://api.krece.app/registration/recargaavailable/': {
		GET: {
			request: undefined | { identification_number: string };
			response: {
				status?: string;
				success?: boolean;
				balance_available: boolean;
				recarga_financed_access?: boolean;
				recarga_sales_access?: boolean;
				first_recharge?: boolean;
				other_purchases?: boolean;
				new_user?: boolean;
				email?: string;
				level?: 'level_1' | 'level_2' | 'level_3' | 'level_4';
			};
		};
	};
	'https://api.krece.app/registration/pendinguserregistration/': {
		POST: {
			request: {
				email: string;
				phone_number: string;
				identification_number: string;
			};
			response: { success?: boolean; status?: string };
		};
	};
	'https://api.krece.app/registration/verifyuseremail/': {
		POST: {
			request: { email: string };
			response: { success?: boolean; status?: string };
		};
	};
	'https://api.krece.app/registration/storerecargaregistration/': {
		POST: {
			request: {
				email: string;
				phone_number: string;
				amount: number;
				operator: string;
				country: string;
				sub_store: string;
				financed?: boolean;
			};
			response: { success?: boolean; status?: string };
		};
	};
	'https://api.krece.app/registration/anexoregistration/': {
		POST: {
			request: {
				seller_store: string;
				store_user: string;
				sub_store: string;
				operator?: string;
				phone_number?: string;
				device_imei: string;
				loan_duration: number; // Number of installments / 2 (quincenales)
				amount_financed: number; // Loan amount
				payment_amount: number; // Installment amount
				device_price: number; // Device price
				self_financed?: boolean; // Self financed
				device_type: string;
				user_document_id: string | null;
				country: string;
				loan_payment_cycle: number;
			};
			response: {
				success: boolean;
				status?: string;
			};
		};
	};
	'https://api.krece.app/payments/processrecargafee/': {
		POST: {
			request: {
				user_document_id: string;
				amount: number;
				date: string;
				country: string;
				api_type: 'BNC';
				reference: string;
				payment_type: 'P2C';
				phone_num: string;
				bank_code: string;
			};
			response: {
				success: boolean;
			};
		};
	};
	'https://api.krece.app/notifications/requestauthcode/': {
		POST: {
			request: {
				code_type: 'whatsapp' | 'sms' | 'email' | 'in_app';
				phone_number?: string;
				email?: string;
				country?: string;
				identification_number?: string;
			};
			response: { success: boolean; status: string };
		};
	};
	'https://api.krece.app/notifications/confirmauthcode/': {
		POST: {
			request: { confirmation_code: number; country: string };
			response: { success: boolean; status: string };
		};
	};
	'https://api.krece.app/notifications/2fa/provision/': {
		POST: {
			response: { success: boolean; status: string; provisioning_uri: string };
		};
	};
	'https://api.krece.app/notifications/2fa/confirm/': {
		POST: {
			request: { code: string };
			response: { success: boolean; status: string };
		};
	};
	'https://api.krece.app/notifications/2fa/verify/': {
		POST: {
			request: { code: string };
			response: { success: boolean; status: string };
		};
	};
	'https://api.krece.app/storeprofile/request_daily_limit_increase/': {
		GET: {
			request: { master_store_email: string };
			response: {
				limit_reached: boolean;
				current_limit: number | null;
				devices_sold: number;
			};
		};
		POST: {
			request: { master_store_email: string };
			response: {
				prior_limit: number | null;
				new_limit: number;
				next_request_eligible_at: string;
			};
		};
	};
	'https://api.krece.app/storeprofile/brand/': {
		GET: {
			request: { email: string };
			response: {
				brand: string;
				instagram: string;
				whatsapp: string;
				logo?: string | null;
			}[];
		};
		POST: {
			request: {
				email: string;
				brands: {
					brand: string;
					instagram: string;
					whatsapp: string;
					logo?: string | null;
				}[];
			};
			response: any;
		};
		DELETE: {
			request: { brand_name: string };
			response: any;
		};
	};
	'https://api.krece.app/storeprofile/branch/': {
		GET: {
			request: { email: string };
			response: {
				[master_store_name: string]: {
					info: {
						logo?: string | null;
						instagram: string;
						instagram_link?: string;
						whatsapp: string;
						whatsapp_link?: string;
					};
					sucursales: {
						logo?: string | null;
						store_email: string;
						store_name: string;
						external_store_name?: string;
						instagram?: string;
						instagram_link?: string;
						whatsapp?: string;
						whatsapp_link?: string;
						city: string;
						location: string;
						location_details?: string;
						location_coordinates?: {
							latitude: number;
							longitude: number;
						};
						hours_of_operation: {
							[key: string]: {
								active: boolean;
								from: number;
								to: number;
							};
						};
						seller_accounts?: {
							email: string;
							first_name: string;
							last_name: string;
						}[];
						is_active?: boolean;
					}[];
				};
			};
		};
		POST: {
			request: {
				email: string;
				store_email: string;
				brand: string;
				store_name: string;
				city: string;
				location: string;
				location_details?: string;
				location_coordinates?: {
					latitude: number;
					longitude: number;
				};
				hours_of_operation: {
					[key: string]: {
						active: boolean;
						from: number;
						to: number;
					};
				};
				instagram?: string;
				whatsapp?: string;
				logo?: string | null;
				is_active?: boolean;
			};
			response: any;
		};
		PUT: {
			request: {
				email: string;
				store_email: string;
				brand: string;
				store_name: string;
				city: string;
				location: string;
				location_details?: string;
				location_coordinates?: {
					latitude: number;
					longitude: number;
				};
				hours_of_operation: {
					[key: string]: {
						active: boolean;
						from: number;
						to: number;
					};
				};
				instagram?: string;
				whatsapp?: string;
				logo?: string | null;
				is_active?: boolean;
			};
			response: any;
		};
		DELETE: {
			request: { email: string; store_email: string };
			response: any;
		};
	};
	'https://api.krece.app/storeprofile/branch/move/': {
		POST: {
			request: {
				email: string;
				store_email: string;
				current_brand: string;
				new_brand: string;
			};
			response: any;
		};
	};
	'https://api.krece.app/storeprofile/branch/seller/': {
		POST: {
			request: {
				brand: string;
				store_email: string;
				seller_account: {
					email: string;
					first_name: string;
					last_name: string;
				};
			};
			response: any;
		};
		DELETE: {
			request: {
				brand: string;
				store_email: string;
				seller_account: {
					email: string;
					first_name: string;
					last_name: string;
				};
			};
			response: any;
		};
	};
	'https://api.krece.app/storeprofile/permissions/': {
		PUT: {
			request: {
				seller_permissions_blacklist?: {
					all_sellers?: string[];
					[email: string]: string[] | undefined;
				};
				seller_permissions_whitelist?: {
					all_sellers?: string[];
					[email: string]: string[] | undefined;
				};
			};
			response: {
				success: boolean;
				status?: string;
			};
		};
	};
	'https://api.krece.app/storeprofile/update_addresses/': {
		PUT: {
			request: {
				master_store: string;
				stores_info: {
					brand: string;
					store_name: string;
					location_details?: string;
					location_coordinates?: {
						latitude: number;
						longitude: number;
					};
				}[];
			};
			response: any;
		};
	};
	'https://api.krece.app/storeprofile/store_payment_methods/': {
		GET: {
			request: { email: string };
			response: {
				transferBs?: {
					rif: string;
					type: string;
					bank: string;
					account: string;
					titular: string;
				};
				pagomovil?: {
					bank: string;
					rif: string;
					phoneNumber: string;
				};
				apolo_client_id?: string;
			};
		};
		PUT: {
			request: {
				master_store_email: string;
				transferBs?: {
					rif: string;
					type: string;
					bank: string;
					account: string;
					titular: string;
				};
				pagomovil?: {
					bank: string;
					rif: string;
					phoneNumber: string;
				};
				apolo_client_id?: string;
			};
			response: any;
		};
	};
	'https://api.krece.app/tables/pre_registered_devices/': {
		POST: {
			request: TableRequest;
			response: TableResponse;
		};
	};
	'https://api.krece.app/tables/payments/': {
		POST: {
			request: TableRequest;
			response: TableResponse;
		};
	};
	'https://api.krece.app/tables/recargas/': {
		POST: {
			request: TableRequest;
			response: TableResponse;
		};
	};
	'https://api.krece.app/tables/devices/': {
		POST: {
			request: TableRequest;
			response: TableResponse;
		};
	};
	'https://api.krece.app/tables/deleted_devices/': {
		POST: {
			request: TableRequest;
			response: TableResponse;
		};
	};
	'https://api.krece.app/tables/transactions/': {
		POST: {
			request: TableRequest;
			response: TableResponse;
		};
	};
	'https://api.krece.app/tables/bank_account_mappings/': {
		POST: {
			request: TableRequest;
			response: TableResponse;
		};
	};
	'https://api.krece.app/tables/bank_movements_conciliation/': {
		POST: {
			request: TableRequest;
			response: TableResponse;
		};
	};
	'https://api.krece.app/tables/bank_movement_category_recommendations/': {
		POST: {
			request: TableRequest;
			response: TableResponse;
		};
	};
	'https://api.krece.app/tables/merchant_payouts/': {
		POST: {
			request: TableRequest;
			response: TableResponse;
		};
	};
	'https://api.krece.app/tables/manual_bank_payments/': {
		POST: {
			request: TableRequest;
			response: TableResponse;
		};
	};
	'https://api.krece.app/tables/proveedores/': {
		POST: {
			request: TableRequest;
			response: TableResponse;
		};
	};
	'https://api.krece.app/tables/insurance_policies/': {
		POST: {
			request: InsurancePoliciesRequest;
			response: { policies: InsurancePolicyRow[]; total_count: number; last_object_id: string | null; has_more: boolean };
		};
	};
	'https://api.krece.app/tables/insurance_claims/': {
		POST: {
			request: InsuranceClaimsRequest;
			response: { claims: InsuranceClaimRow[]; total_count: number; last_object_id: string | null; has_more: boolean };
		};
	};
	'https://api.krece.app/update_siniestro_status/': {
		POST: {
			request: UpdateSiniestroStatusRequest;
			response: UpdateSiniestroStatusResponse;
		};
	};
	'https://api.krece.app/siniestro_desincorporation_letter/': {
		POST: {
			request: { claim_id: string };
			response: { filename: string; content_type: 'application/pdf'; pdf_base64: string };
		};
	};
	'https://api.krece.app/bank_account_mappings/': {
		GET: {
			request: undefined;
			response: {
				success: boolean;
				valid_types: string[];
				valid_directions: string[];
				valid_categories: string[];
				valid_categories_by_direction: Record<string, string[]>;
			};
		};
	};
	'https://api.krece.app/bank_movement_reclassify/': {
		POST: {
			request:
				| {
						document_id: string;
						transaction_type: string;
						bank: string;
						category: string;
						reason: string;
				  }
				| {
						movements: { document_id: string; transaction_type: string; bank: string }[];
						category: string;
						reason: string;
				  };
			response: {
				success: boolean;
				status: string;
				changed: { document_id: string; old_category?: string | null; new_category: string }[];
				reclassified_by_email: string;
				reclassified_by_name: string;
				reclassified_at: string;
			};
		};
	};
	'https://api.krece.app/bank_movement_category_approvals/': {
		GET: {
			request: undefined;
			response: {
				success: boolean;
				approvals: {
					id: string;
					document_id: string;
					old_category?: string | null;
					new_category: string;
					reason: string;
					requested_by: string;
					requested_at?: string | null;
				}[];
			};
		};
		POST: {
			request: { approval_id: string; action: 'approve' | 'reject' };
			response: { success: boolean; status: string };
		};
	};
	'https://api.krece.app/bank_movement_category_recommendations/bulk/': {
		POST: {
			request: {
				action: 'approve' | 'reject';
				recommendations: { document_id: string; transaction_type: string }[];
			};
			response: {
				success: boolean;
				status: string;
				job_id: string;
				task_id: string;
				total_requested: number;
			};
		};
	};
	'https://api.krece.app/bank_movement_category_recommendation_groups/': {
		GET: {
			request: undefined | { min_count?: number; limit?: number };
			response: {
				success: boolean;
				country: string;
				total_pending: number;
				total_groups: number;
				groups: {
					group_key: string;
					transaction_type: string;
					bank: string;
					current_category: string;
					suggested_category: string;
					reason: string;
					evidence: string;
					concept: string;
					count: number;
					total_amount: number;
					avg_confidence?: number | null;
					first_date?: string | null;
					last_date?: string | null;
					job_ids: string[];
					sample_documents: { document_id: string; collection: string; amount?: number | string | null; date?: string | null }[];
				}[];
			};
		};
		POST: {
			request: Record<string, never>;
			response: { success: boolean; status: string; task_id: string };
		};
	};
	'https://api.krece.app/bank_movement_category_recommendation_groups/items/': {
		GET: {
			request: undefined | { group_key: string; transaction_type?: string; limit?: number; cursor?: string };
			response: {
				success: boolean;
				country: string;
				group_key: string;
				next_cursor?: string | null;
				items: {
					document_id: string;
					collection: string;
					bank: string;
					transaction_type: string;
					date?: string | null;
					amount?: number | string | null;
					account_or_rif?: string | null;
					reference?: string | null;
					concept?: string | null;
					description?: string | null;
					current_category?: string | null;
					suggested_category?: string | null;
					confidence?: number | null;
					reason?: string | null;
					evidence?: string | null;
					job_id?: string | null;
					generated_at?: string | null;
				}[];
			};
		};
	};
	'https://api.krece.app/bank_movement_category_recommendation_groups/resolve/': {
		POST: {
			request: { action: 'approve' | 'reject'; group_key: string; group_payload?: Record<string, string | number | null> };
			response: { success: boolean; status: string; job_id: string; task_id: string };
		};
	};
	'https://api.krece.app/bank_movement_category_recommendations/': {
		GET: {
			request: undefined | { status?: 'all' | 'pending' | 'approved' | 'rejected' };
			response: {
				success: boolean;
				recommendations: {
					document_id: string;
					bank: string;
					transaction_type: string;
					date?: string | null;
					amount?: number | null;
					concept?: string | null;
					current_category?: string | null;
					suggested_category: string;
					status?: 'pending' | 'approved' | 'rejected' | null;
					confidence?: number | null;
					reason?: string | null;
					evidence?: string | null;
					job_id?: string | null;
					generated_at?: string | null;
					reviewed_by?: string | null;
					reviewed_at?: string | null;
				}[];
			};
		};
		POST: {
			request: { document_id: string; transaction_type: string; action: 'approve' | 'reject' };
			response: { success: boolean; status: string };
		};
	};
	'https://api.krece.app/tables/delinquent_users/': {
		POST: {
			request: TableRequest;
			response: TableResponse;
		};
	};
	'https://api.krece.app/tables/devicespagados/': {
		POST: {
			request: TableRequest;
			response: TableResponse;
		};
	};
	'https://api.krece.app/tables/unverified_payments/': {
		POST: {
			request: TableRequest;
			response: TableResponse;
		};
	};
	'https://api.krece.app/tables/storesummarydata/': {
		POST: {
			request: {
				master_store_email: string;
				filter_by?: { column_name: string; value: string }[];
				date_range:
					| { from_date: string; to_date: string }
					| {
							custom_range: 'hoy' | 'ayer' | 'antiayer' | 'semana actual' | 'semana anterior' | 'corte actual' | 'corte anterior';
					  };
			};
			response: [
				{
					ventas: number;
					monto_financiado: number;
					pagos_en_tienda: number;
				}
			];
		};
	};
	'https://api.krece.app/storeprofile/integrations/webhook/': {
		GET: {
			request: undefined;
			response: { webhook: StoreWebhookConfig | null };
		};
		PUT: {
			request: {
				url: string;
				is_active: boolean;
				event_types: StoreWebhookEventType[];
			};
			response: { webhook: StoreWebhookConfig; signing_secret?: string };
		};
	};
	'https://api.krece.app/storeprofile/integrations/webhook/test/': {
		POST: {
			request: undefined;
			response: {
				delivered: boolean;
				error_code: string | null;
				detail: string | null;
				response_status: number | null;
			};
		};
	};
	'https://api.krece.app/storeprofile/integrations/webhook/rotate-secret/': {
		POST: {
			request: undefined;
			response: { webhook: StoreWebhookConfig; signing_secret: string };
		};
	};
	'https://api.krece.app/storeprofile/integrations/webhook/deliveries/': {
		GET: {
			request: { limit?: number };
			response: { deliveries: StoreWebhookDelivery[] };
		};
	};
	'https://api.krece.app/storeprofile/store_health/': {
		GET: {
			request: { history_period?: '30d' | '3m' | '6m' | '1y' | 'max' };
			response: {
				health: StoreHealthSnapshot | null;
				history: StoreHealthHistoryPoint[];
			};
		};
	};
	'https://api.krece.app/storeprofile/store_health_summary/': {
		GET: {
			request: undefined;
			response: StoreHealthSummary;
		};
	};
	'https://api.krece.app/storeprofile/elite_financing_options/': {
		GET: {
			request: { sub_store?: string };
			response: EliteFinancingOptions;
		};
	};
	'https://api.krece.app/storeprofile/masteruser/': {
		GET: {
			request: undefined;
			response: string[];
		};
		PUT: {
			request: {
				email: string;

				company_name?: string;
				first_name?: string;
				last_name?: string;
				phone_number?: string;
				identification_number?: string;
				identification_url?: string;
				identification_type?: string;
				identification_number_2?: string;
				identification_url_2?: string;
				identification_type_2?: string;

				bank?: string;
				bank_account_holder?: string;
				bank_account_type?: 'corriente' | 'ahorro';
				bank_account_number?: string;
				interbank_code?: string;
				bank_identification_number?: string;
				bank_identification_type?:
					| 'nit'
					| 'rif'
					| 'cedula'
					| 'dni'
					| 'rnc'
					| 'ruc'
					| 'firma_personal'
					| 'extranjera'
					| 'passport';
			};
			response: any;
		};
	};
	'https://api.krece.app/storeprofile/update_registration_steps/': {
		POST: {
			request: { master_store_email: string; registration_steps: UserData['registration_steps'] };
			response: any;
		};
	};
	'https://api.krece.app/storeprofile/update_balance_threshold/': {
		POST: {
			request: {
				balance_threshold: number;
				store_email: string;
			};
			response: any;
		};
	};
	'https://api.krece.app/payments/libelulaurl/': {
		POST: {
			request: {
				amount: number;
				identification_number: string;
				payment_object_id: string;
				email: string;
			};
			response: {
				status?: string;
				success?: boolean;
				checkout_url?: string;
				session_id?: string;
			};
		};
	};
	'https://api.krece.app/payments/prematchunverifiedpayment/': {
		POST: {
			request: { unverified_payment_id: string };
			response: {
				prematch: {
					algorithm_version: string;
					status: 'recommended' | 'ambiguous' | 'no_match' | 'invalid_payment_data';
					confidence: 'high' | 'medium' | 'low' | null;
					candidate_count: number;
					recommended_candidate_id: string | null;
					candidate_ids: string[];
					top_score: number | null;
					score_margin: number | null;
					evidence: string[];
					auto_eligible: boolean;
					bank_window_truncated: boolean;
					shadow_mode: true;
					candidates: {
						id: string;
						amount: number;
						date: string;
						bank: string;
						description: string;
						reference: string;
						bank_reference: string;
						serial: string;
						used: boolean;
					}[];
				};
			};
		};
	};
	'https://api.krece.app/payments/processunverifiedpayment/': {
		PUT: {
			request: {
				date?: string;
				amount?: number;
				unverified_payment_id: string;
				reference?: string;
				bank_id?: string;
				bank_account_number?: string;
				api_type?: 'bnc' | 'mercantil' | 'r4';
				payment_type?: 'p2c' | 'transfer';
				phone_num?: string;
				issuer_bank_id?: string;
				issuer_customer_id?: string;
				accept_payment?: false;
			};
			response: any;
		};
		POST: {
			request: {
				user_document_id: string;
				payment_object_id: string;
				amount: number;
				date: string;
				country: string;
				payment_confirmation_url: string;
				reference: string;
				account_number: string;
				account_owner: string;
			};
			response: any;
		};
	};
	'https://api.krece.app/storeprofile/perfil/2fa-phone/': {
		PATCH: {
			request: {
				phone_number: string;
				confirmation_code: number;
			};
			response: {
				success: boolean;
				status: string;
				phone_number?: string;
			};
		};
	};
	'https://api.krece.app/storeprofile/get_custom_token/': {
		POST: {
			request: { email: string };
			response: string;
		};
	};
	'https://api.krece.app/registration/is_valid_id/': {
		POST: {
			request: { id: string; object_type: 'Devices' | 'Payments' | 'Transactions' };
			response: any;
		};
	};
	'https://api.krece.app/registration/cambiodegarantia/': {
		POST: {
			request:
				| {
						identification_number: string;
						garantia_action: 'list' | 'devices' | 'eligible_devices';
						liveness_document_id: string;
				  }
				| {
						identification_number: string;
						garantia_action: 'check';
						confirmation_id?: string;
						device_document_id?: string;
				  }
				| {
						identification_number: string;
						garantia_action: 'generate_warranty_contract';
						device_document_id: string;
						target_imei: string;
						liveness_document_id: string;
				  }
				| {
						identification_number: string;
						garantia_action: 'change';
						device_document_id: string;
						target_imei: string;
						liveness_document_id: string;
						anexo_document_id?: string;
				  }
				| {
						identification_number: string;
						garantia_action: 'complete_paid_device_exchange';
						device_document_id: string;
						original_imei: string;
						target_imei: string;
						liveness_document_id: string;
				  }
				| {
						identification_number: string;
						garantia_action: 'list_warranty_devices' | 'list_technical_service_devices';
						liveness_document_id: string;
				  }
				| {
						identification_number: string;
						garantia_action: 'complete_technical_service_reenrollment';
						device_document_id: string;
						target_imei: string;
						liveness_document_id: string;
				  };
			response: {
				devices?: WarrantyDevice[];
				user_document_id?: string;
				device?: WarrantyDevice;
				device_document_id?: string;
				device_type?: 'iphone' | 'phone' | 'tablet';
				device_imei_no?: string;
				device_commercial_name?: string;
				date_bought?: string | null;
				original_imei?: string;
				target_imei?: string;
				fully_paid?: boolean;
				device_management_provider?: string;
				anexo_document_id?: string;
				terms_accepted?: boolean;
			};
		};
	};
	'https://api.krece.app/registration/preregistereddevices/': {
		GET: {
			request: {
				id: string;
			};
			response: {
				success?: boolean;
				status?: string;
				data?: {
					device_type: 'iphone' | 'phone' | 'tablet' | 'laptop';
					amount_financed: number;
					device_loan_duration: number;
					user_document_id: string;
					identification_number: string;
					seller_store: string;
					device_price: string;
					loan_duration: number;
					payment_amount: number;
					sub_store: string;
					store_user: string;
					promotional_code?: string;
					device_manufacturer?: string;
				};
			};
		};
		POST: {
			request: {
				user_document_id: string;
				identification_number: string;
				seller_store: string;
				device_price: string;
				loan_duration: number;
				payment_data: {
					loan_payment_cycle: 15;
					payment_amount: number;
				};
				amount_financed: number;
				sub_store: string;
				store_user: string;
				device_type: 'iphone' | 'phone' | 'tablet' | 'laptop';
				promotional_code?: string;
				device_manufacturer?: string;
				country: string;
			};
			response: {
				success?: boolean;
				status?: string;
			};
		};
		DELETE: {
			request: {
				id: string;
			};
			response: {
				success?: boolean;
				status?: string;
			};
		};
	};
	'https://api.krece.app/registration/registerimei/': {
		POST: {
			request: {
				device_imei: string;
				device_type: 'iphone' | 'phone' | 'tablet' | 'laptop';
				device_management_provider: 'absolute' | 'tonic';
			};
			response: any;
		};
	};
	'https://api.krece.app/registration/registerimeiv2/': {
		POST: {
			request: {
				device_imei: string;
				device_type: 'iphone' | 'phone' | 'tablet' | 'laptop';
				device_price?: number;
				country: string;
				supports_installation?: boolean;
				installation_completed?: boolean;
				activation_id?: string;
				device_manufacturer?: string;
				technical_service_device_document_id?: string;
				technical_service_identification_number?: string;
				technical_service_liveness_document_id?: string;
				warranty_exchange_device_document_id?: string;
				warranty_exchange_identification_number?: string;
				warranty_exchange_liveness_document_id?: string;
			};
			response: {
				status?: string;
				activating_status?: 'activated' | 'confirming' | 'failed' | 'installation_required';
				activation_id?: string;
				activation_expired?: boolean;
				device_management_provider?: string;
				upload_id?: string;
			};
		};
	};
	'https://api.krece.app/payments/getdiscountedpayments/': {
		POST: {
			request: {
				country: string;
				device_object_id: string;
			};
			response: {
				new_total_pending_amount: string;
			};
		};
	};
	'https://api.krece.app/users/financing-overrides/': {
		GET: {
			request: { user_email: string; product_type?: string; amount_financed?: number; installments?: number };
			response: {
				financing_overrides: ActiveFinancingOverrides & { expires_at?: string };
				/** What the backend applies right now ({} once expires_at has passed) */
				active_financing_overrides: ActiveFinancingOverrides;
				/** Exact cuota the backend will accept; null when that product has no cuota override */
				quote?: { payment_amount: number; factor?: number; installments: number; amount_financed: number } | null;
			};
		};
		/** Admins only. Replaces the whole map ({} removes it); expected is what the last GET returned (null replaces an invalid map) */
		PUT: {
			request: {
				user_email: string;
				financing_overrides: ActiveFinancingOverrides & { expires_at?: string };
				expected_financing_overrides: (ActiveFinancingOverrides & { expires_at?: string }) | null;
			};
			response: { financing_overrides: ActiveFinancingOverrides & { expires_at?: string } };
		};
	};
	'https://api.krece.app/users/factormultiplier/': {
		POST: {
			request: {
				user_document_id: string;
				device_price: number;
				device_type: 'iphone' | 'phone' | 'tablet' | 'laptop' | 'other' | 'loan';
				device_manufacturer: string | null;
				installments: number;
				down_payment_percentage: number; // 0 to 1
				level: string;
			};
			response: {
				factor_multiplier: number;
				promo_eligibility: boolean;
				down_payment_pct?: number;
			};
		};
	};
	'https://api.krece.app/authentication/liveness-detection-session/': {
		GET: {
			request: any;
			response: {
				session_id: string;
				document_id: string;
				link_expired?: boolean;
			};
		};
		POST: {
			request: {
				liveness_session_id: string;
			};
			response: {
				user_authenticated: boolean;
				valid_user: boolean;
				photo_id_url?: string;
			};
		};
	};
	'https://api.krece.app/process_refund/': {
		POST: {
			request: {
				id_document: string;
				transaction_id: string;
				account_number: string;
				refund_amount: number;
			};
			response: any;
		};
	};
	'https://api.krece.app/split_payment/': {
		POST: {
			request: {
				number_of_installments: number;
				device_id: string;
			};
			response: any;
		};
	};
	'https://api.krece.app/administration/lastsuccessfulliveness/': {
		GET: {
			request: {
				user_email: string;
			};
			response: {
				success?: boolean;
				status?: string;
				successful_liveness?: boolean;
			};
		};
		POST: {
			request: {
				user_email: string;
			};
			response: {
				success?: boolean;
				status?: string;
			};
		};
	};
	'https://api.krece.app/generate_payment_files/': {
		POST: {
			request: {
				start_date: string;
				end_date: string;
				include_stores?: string[];
				exclude_stores?: string[];
			};
			response: any;
		};
	};
	'https://api.krece.app/send_payment_summary/': {
		POST: {
			request: {
				start_date: string;
				end_date: string;
				include_stores?: string[];
				exclude_stores?: string[];
			};
			response: any;
		};
	};
	'https://api.krece.app/send_api_payment/': {
		POST: {
			request: {
				id_document: string;
				amount: number;
				account_number: string;
			};
			response: any;
		};
	};
	'https://api.krece.app/push_notif/': {
		POST: {
			request: {
				name: string;
				title: string;
				body: string;
				frequency: 'one_time' | 'daily' | 'weekly' | 'monthly';
				scheduled_date: string;
				query: string;
				country: string;
			};
			response: any;
		};
		PATCH: {
			// Add the doc id url to the url
			request: {
				name: string;
				title: string;
				body: string;
				frequency: 'one_time' | 'daily' | 'weekly' | 'monthly';
				scheduled_date: string;
				query: string;
				country: string;
			};
			response: any;
		};
		DELETE: {
			// Add the doc id url to the url
			request: undefined;
			response: any;
		};
	};
	'https://api.krece.app/administration/deletedevice/': {
		POST: {
			request: {
				identification_number: string;
				store_user: string;
				country: string;
				device_id?: string;
				imei_no?: string;
			};
			response: {
				device_deleted: boolean;
			};
		};
	};
	'https://api.krece.app/administration/change_user_email/': {
		POST: {
			request: {
				email: string;
				new_email: string;
				identification_number: string;
			};
			response: {
				success: boolean;
				status: string;
			};
		};
	};
	'https://api.krece.app/fraud_transfer_device/': {
		POST: {
			request: {
				imei: string;
				identification_number_1: string;
				identification_number_2: string;
			};
			response: {
				success: boolean;
				status: string;
				fraud_device_id: string;
				device_id: string;
				ghost_device_id: string;
				reminders_transferred: number;
				payments_transferred: number;
				frozen_payments_created: number;
			};
		};
	};
	'https://api.krece.app/administration/price_control_override/': {
		POST: {
			request: {
				country: string;
				imei: string;
				amount: number;
			};
			response: {
				id: string;
				country: string;
				identifier_type: 'imei';
				identifier: string;
				max_price: number;
				active: boolean;
				updated_at?: string;
				updated_by?: string;
			};
		};
	};
	'https://api.krece.app/lock_device/': {
		POST: {
			request: { device_id: string };
			response: {
				success: boolean;
				status?: string;
				detail?: string;
			};
		};
	};
	'https://api.krece.app/unlock_phone/': {
		POST: {
			request: { device_imei_no: string };
			response: {
				success: boolean;
				status?: string;
			};
		};
	};
	'https://api.krece.app/edit_phone_number/': {
		POST: {
			request: {
				identification_number: string;
				incorrect_phone_number: string;
				new_phone_number: string;
			};
			response: any;
		};
	};
	'https://api.krece.app/users/emailbyid/': {
		POST: {
			request: {
				identification_number: string;
			};
			response: {
				email?: string;
				success?: boolean;
				status?: string;
			};
		};
	};
	'https://api.krece.app/users/idbyemail/': {
		POST: {
			request: {
				email: string;
			};
			response: {
				identification_number?: string;
				success?: boolean;
				status?: string;
			};
		};
	};
	'https://api.krece.app/users/<email_or_identification_number>/': {
		GET: {
			request: undefined;
			response: {
				identification_number?: string;
				email?: string;
				level?:
					| 'level_1'
					| 'level_2'
					| 'level_3'
					| 'level_4'
					| 'level_5'
					| 'level_6'
					| 'level_7'
					| 'level_8'
					| 'level_9'
					| 'level_10';
			};
		};
	};
	'https://api.krece.app/administration/async_delete_loans/': {
		POST: {
			request: {
				device_ids: string[];
			};
			response: {
				status?: string;
				success?: boolean;
			};
		};
	};
	'https://api.krece.app/change_device_selling_price_with_code/': {
		POST: {
			request: {
				confirmation_code: string;
				device_id: string;
				master_store_email: string;
				device_price: string;
				country: string;
			};
			response: {
				success: boolean;
				status?: string;
			};
		};
	};
	'https://api.krece.app/delete_payment_with_code/': {
		POST: {
			request: {
				confirmation_code: string;
				confirmation_number: string;
				master_store_email: string;
				country: string;
			};
			response: {
				success: boolean;
				status?: string;
			};
		};
	};
	'https://api.krece.app/storeprofile/devices_models/': {
		GET: {
			request: {
				device_model_id?: string;
				device_manufacturer?: string;
				last_document_id?: string;
				limit?: number;
			};
			response: {
				id: string;
				brand: string;
				model: string;
				device_commercial_name: string;
				available_colors?: { name: string; hex: string }[];
				display?: string;
				storage?: number;
				ram?: number;
				processor?: string;
				battery?: string;
				created_at: string;
				updated_at: string;
			}[];
		};
	};
	'https://api.krece.app/storeprofile/store_products/': {
		GET: {
			request: {
				product_id?: string;
				search?: string;
				price_min?: number;
				price_max?: number;
				last_document_id?: string;
				limit?: number;
			};
			response: {
				id: string;
				price: number;
				available: boolean;
				quantity: number | null;
				product_info: {
					brand: string;
					model: string;
					device_commercial_name: string;
					display?: string;
					storage?: number;
					ram?: number;
					processor?: string;
					battery?: string;
					available_colors?: { name: string; hex: string }[];
					created_at: string;
					updated_at: string;
				};
			}[];
		};
		POST: {
			request: {
				related_device_model_id?: string;
				name?: string;
				available?: boolean;
				price?: number;
				quantity?: number | null;
				products?: {
					related_device_model_id: string;
					name: string;
					available: boolean;
					price: number;
					quantity?: number | null;
				}[];
			};
			response: {
				success: boolean;
				status: string;
			};
		};
		PUT: {
			request: {
				product_id: string;
				name?: string;
				available?: boolean;
				price?: number | null;
				quantity?: number | null;
			};
			response: {
				id: string;
				name: string;
				price: number | null;
				quantity: number | null;
				available: boolean;
				store: string;
				product_id: string;
				related_device_model: string;
			};
		};
		DELETE: {
			request: {
				product_id: string;
			};
			response: {
				success: boolean;
				products_deleted?: boolean;
				status: string;
			};
		};
	};
	'https://api.krece.app/storeprofile/store_product_delete_from_catalogs/': {
		POST: {
			request: {
				product_id: string;
			};
			response: {
				success: boolean;
			};
		};
	};
	'https://api.krece.app/storeprofile/online_store_name/': {
		POST: {
			request: {
				online_store_name: string;
			};
			response: {
				online_store_name: string;
			};
		};
	};
	'https://api.krece.app/storeprofile/store_catalogs/': {
		GET: {
			request: {
				catalog_id?: string;
			};
			// List returns { success, catalogs: [...] } (no data key, so not extracted)
			// Single returns { success, data: catalog } (data is extracted)
			response: {
				success: boolean;
				catalogs: {
					catalog_id: string;
					store_email: string;
					name: string;
					description?: string | null;
					product_ids: string[];
					is_active: boolean;
					created_at: string;
					updated_at: string;
				}[];
			} | {
				catalog_id: string;
				store_email: string;
				name: string;
				description?: string | null;
				product_ids: string[];
				is_active: boolean;
				created_at: string;
				updated_at: string;
			};
		};
		POST: {
			request: {
				name: string;
				description?: string;
				product_ids: string[];
			};
			response: {
				success: boolean;
				catalog_id: string;
				status: string;
			};
		};
		PUT: {
			request: {
				catalog_id: string;
				name?: string;
				description?: string;
				product_ids?: string[];
				is_active?: boolean;
			};
			response: {
				catalog_id: string;
				store_email: string;
				name: string;
				description: string | null;
				product_ids: string[];
				is_active: boolean;
				created_at: string;
				updated_at: string;
			};
		};
		DELETE: {
			request: {
				catalog_id: string;
			};
			response: {
				success: boolean;
				status: string;
			};
		};
	},
	'https://api.krece.app/administration/kandjicheckenrollperiod/': {
		POST: {
			request: {
				devices: { device_imei_no: string }[];
			};
			response: {
				success?: boolean;
				status?: string;
				data: {
					success?: boolean;
					status?: string;
					device_imei_no?: string;
					enroll_period?: boolean;
					start_date?: string; // YYYY-MM-DD
				}[];
			};
		};
	},
	'https://api.krece.app/administration/teardownappledevice/': {
		POST: {
			request: {
				device_imei_no: string;
				confirm_erase: true;
			};
			response: {
				teardown_status: 'erase_sent' | 'completed';
				bypass_code: string | null;
				serial_no?: string;
			};
		};
	},
	'https://api.krece.app/marketplace/offers/<offer_id>/respond': {
		POST: {
			request: {
				action: 'accept' | 'reject';
				reason?: 'out_of_stock' | 'price_too_low' | 'other';
				reason_details?: string;
			};
			response: { success?: boolean; status?: string };
		};
	},
	'https://api.krece.app/administration/store-popups/active/': {
		GET: {
			request: { country: string };
			response: StorePopupRecord | null;
		};
	};
	'https://api.krece.app/administration/store-popups/': {
		GET: {
			request: { country: string };
			response: StorePopupRecord[];
		};
		POST: {
			request: Required<Pick<StorePopupPayload, 'country' | 'title' | 'is_active' | 'dismiss_interval_days'>> & StorePopupPayload;
			response: StorePopupRecord;
		};
	};
	'https://api.krece.app/administration/store-popups/upload-image/': {
		POST: {
			request: FormData;
			response: { image_url: string };
		};
	};
	'https://api.krece.app/administration/store-popups/<popup_id>/': {
		PATCH: {
			request: StorePopupPayload;
			response: StorePopupRecord;
		};
	};
	'https://api.krece.app/storeprofile/perfil/': {
		GET: {
			response: {
				store_name: string;
				email: string;
				phone_number: string;
				bank_account: {
					bank_name: string;
					account_name: string;
					account_number: string;
					account_type: string;
					document_type: string;
					identification_number: string;
					interbank_code: string;
				};
				forward_email_address: string[];
				sub_stores_count: number;
				last_modified_date: string | null;
				country: string;
				has_pending_bank_request: boolean;
				limit_count: number | null;
			};
		};
	};
	'https://api.krece.app/storeprofile/perfil/phone/': {
		PATCH: {
			request: { phone_number: string; verification_token: string };
			response: { phone_number: string };
		};
	};
	'https://api.krece.app/storeprofile/perfil/forward-email/': {
		PATCH: {
			request: { forward_email_address: string[] };
			response: { forward_email_address: string[] };
		};
	};
	'https://api.krece.app/storeprofile/perfil/bank-change-request/': {
		POST: {
			request: {
				new_bank_info: {
					bank_name: string;
					account_name: string;
					account_number: string;
					account_type: string;
					document_type?: string;
					identification_number?: string;
					interbank_code?: string;
				};
			};
			response: { request_id: string };
		};
	};
	'https://api.krece.app/notifications/confirmauthcode-token/': {
		POST: {
			request: { confirmation_code: number; identification_number?: string; country?: string };
			response: { success: boolean; verification_token?: string };
		};
	};
}

export type APIEndpoints = keyof API;

export interface TableRequest {
	master_store_email: string | null;
	last_object_id: null | string;
	filter_by?: {
		column_name: string;
		value: string | boolean;
	}[];
	order_by?: {
		column_name: string;
		column_order: 'ASC' | 'DESC';
	};
	search_by?: {
		column_name: string;
		value: string | string[];
	};
	date_range?:
		| { from_date: string; to_date: string }
		| {
				custom_range: 'Ver todo' | 'Hoy' | 'Ayer' | 'Semana actual' | 'Última semana' | 'Este mes a la fecha' | 'Últimos 30 días';
		  };
	batch_size?: number;
	download_file?: boolean;
}
export interface TableResponse {
	last_object_id: null | string;
	column_names: string[];
	rows: string[][];
	order_by: {
		column_name: string;
		column_order: 'ASC' | 'DESC';
	};
	total_count: number;
	download_link?: string;
}

export type InsurancePoliciesRequest = {
	country: string;
	status_filter: 'all' | 'active' | 'inactive';
	search: string;
	order: 'asc' | 'desc';
	last_object_id?: string | null;
	page_index?: number;
	batch_size?: number;
};

export type InsuranceClaimsRequest = {
	country: string;
	status_filter: 'all' | 'RECIBIDO' | 'EN_ANALISIS' | 'APROBADO' | 'RECHAZADO' | 'PENDIENTE_INFO';
	search: string;
	order: 'asc' | 'desc';
	last_object_id?: string | null;
	page_index?: number;
	batch_size?: number;
};

export type InsurancePolicyRow = {
	id: string;
	deviceId: string;
	policyNumber: string;
	certificateNumber: string;
	status: string;
	statusLabel: string;
	state: 'active' | 'inactive';
	issuedAt: string | null;
	validUntil: string | null;
	customerEmail: string;
	sellerStore: string;
	subStore: string;
	storeUser: string;
	deviceLabel: string;
	deviceType: string;
	activeClaim: string;
};

export type InsuranceClaimRow = {
	id: string;
	siniestroId: string;
	deviceId: string;
	policyNumber: string;
	status: string;
	statusLabel: string;
	type: string;
	customerEmail: string;
	phone: string;
	secondaryPhoneNumber: string;
	occurredAt: string;
	createdAt: string | null;
	updatedAt: string | null;
	resolvedAt: string | null;
	place: string;
	description: string;
	observations: string;
	approvedAmount: string;
	deviceLabel: string;
	deviceImei: string;
	supportUrl: string;
};

export type UpdateSiniestroStatusRequest = {
	claim_id: string;
	status: 'APROBADO' | 'RECHAZADO';
	observaciones?: string;
};

export type UpdateSiniestroStatusResponse = {
	claim_id: string;
	siniestro_id?: string;
	device_id: string;
	status: 'APROBADO' | 'RECHAZADO';
	device_locked: boolean;
	payment_ids_paid_by_insurance?: string[];
	amount_paid_by_insurance?: number;
};
